import clsx from "clsx";
import type { ReactNode } from "react";

export interface Column<Row> {
  key: string;
  header: ReactNode;
  /** Cell renderer; defaults to `String(row[key])` when omitted. */
  cell?: (row: Row) => ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
}

export interface TableProps<Row> {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row, index: number) => string;
  caption?: ReactNode;
  empty?: ReactNode;
  className?: string;
}

const ALIGN = { left: "text-left", right: "text-right", center: "text-center" };

/** A plain data table. Wrapped in its own horizontal scroller on small screens. */
export function Table<Row>({ columns, rows, rowKey, caption, empty, className }: TableProps<Row>) {
  if (rows.length === 0 && empty) return <>{empty}</>;
  return (
    // `relative`: an sr-only header (`<span className="sr-only">`) is
    // `position: absolute`, and without a positioned ancestor it escapes
    // this scroller entirely, widening the *page* instead of just this box
    // (UX-05 — the exam lobby's hidden "Åtgärd" header at 390 px).
    <div className={clsx("relative w-full overflow-x-auto", className)}>
      <table className="w-full border-collapse text-sm">
        {caption && (
          <caption className="pb-2 text-left text-sm text-ink-600 dark:text-ink-400">
            {caption}
          </caption>
        )}
        <thead>
          <tr className="border-b border-ink-200 dark:border-ink-800">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={clsx(
                  "px-3 py-2 font-medium text-ink-600 dark:text-ink-300",
                  ALIGN[column.align ?? "left"],
                  column.className,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={rowKey(row, index)}
              className="border-b border-ink-100 last:border-0 dark:border-ink-900"
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={clsx("px-3 py-2", ALIGN[column.align ?? "left"], column.className)}
                >
                  {column.cell
                    ? column.cell(row)
                    : String((row as Record<string, unknown>)[column.key] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
